import LoopSeriesPanel from "./loop-series-panel";
import { getUserInitials } from "@/utils";
import { Avatar, Button, Card, Popover, Tabs } from "@heroui/react";
import { Ellipsis, LogIn, Mic, Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession, useUser } from "@clerk/react";
import LoopOverviewPanel from "./loop-details-panels/loop-overview-panel";
import ComingSoonComp from "@/components/ui/coming-soon-comp";
import LoopDetailsSkeleton from "../skeletons/loop-details-skeleton";
import CountdownBadge from "../countdown-badge";
import { getByDocId } from "@/api/meets";
import { isMeetingActive, isMeetingPast, isMeetingUpcoming } from "@/utils/dates";
import EditMeetingDetailsDrawer from "../drawers/edit-meeting-drawer";

const TABS = {
  overview: { key: "overview", label: "Overview" },
  meetings: { key: "meetings", label: "Meetings" },
  recordings: { key: "recordings", label: "Recordings" },
  details: { key: "details", label: "Details" },
};

const LoopDetailsComp = ({ _loop_id, refreshKey = 0, onClose }) => {
  const { session } = useSession();
  const { user } = useUser();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");
  const [record, setRecord] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);

  useEffect(() => {
    setRecord(null);
    setNotFound(false);
  }, [_loop_id]);

  useEffect(() => {
    if (!_loop_id || _loop_id === "") return;

    let cancelled = false;

    const load = async () => {
      const token = await session.getToken();

      getByDocId(_loop_id, token)
        .then((_resp) => {
          if (cancelled) return;
          if (_resp.data.Result === "OK") {
            setRecord(_resp.data.Record);
          } else {
            setNotFound(true);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          console.error(err);
          setNotFound(true);
        });
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_loop_id, refreshKey, localRefresh]);

  if (notFound)
    return (
      <Card className="shadow-none border">
        <Card.Content className="flex flex-col items-center gap-3 py-12 text-center">
          <div className="rounded-full bg-default/60 p-4">
            <Mic className="size-6 text-muted" />
          </div>
          <div>
            <p className="font-semibold">Loop not found</p>
            <p className="text-sm text-muted">It may have been deleted or moved.</p>
          </div>
        </Card.Content>
      </Card>
    );

  if (!record) return <LoopDetailsSkeleton />;

  const shortCode = record["short_code"];
  const startTime = record["start_time"]?.set;
  // `real` stays "" until the meeting actually ends, so fall back to the slot.
  const endTime = record["end_time"]?.real || record["end_time"]?.set;

  const isActive = Boolean(startTime && endTime) && isMeetingActive(record["meeting_date"], startTime, endTime);
  const isUpcoming = Boolean(startTime) && isMeetingUpcoming(record["meeting_date"], startTime);
  const isPast = Boolean(endTime) && isMeetingPast(record["meeting_date"], endTime);

  const isHost = user?.id && record?.created_by?.user_id && user.id === record.created_by.user_id;

  const handlePrimaryAction = () => {
    if (isUpcoming && isHost) {
      setShowEdit(true);
      return;
    }
    if (!shortCode) return;
    if (isActive && isHost) return navigate(`/meetings/join/${shortCode}`);
    if (isPast) return navigate(`/recordings?selected=${encodeURIComponent(record._id)}`);
    setActiveTab("overview");
  };

  return (
    <>
      <Card className="shadow-none border">
        <Card.Header className="flex-row gap-4 relative">
          <Avatar size="lg" color="accent" variant="soft">
            <Avatar.Fallback>{getUserInitials(record["title"] || "")}</Avatar.Fallback>
          </Avatar>

          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold truncate">{record["title"]}</span>
              <CountdownBadge loop={record} />
            </div>
            <span className="text-muted text-sm">{record["description"] || "Loop description"}</span>
          </div>

          <div className="flex items-center gap-0 absolute inset-e-0">
            <Popover>
              <Popover.Trigger>
                <Button isIconOnly variant="ghost">
                  <Ellipsis />
                </Button>
              </Popover.Trigger>
            </Popover>

            <Button isIconOnly variant="ghost" onPress={onClose} aria-label="Close details">
              <X />
            </Button>
          </div>
        </Card.Header>

        <Card.Content className="gap-4">
          {/* Primary action — live loops get a join, otherwise edit or view */}
          {isActive ? (
            <Button variant="primary" fullWidth onPress={handlePrimaryAction}>
              <LogIn size={16} />
              {isHost ? "Join now" : "Open loop"}
            </Button>
          ) : isUpcoming && isHost ? (
            <Button variant="outline" fullWidth onPress={handlePrimaryAction}>
              <Pencil size={16} />
              Edit loop
            </Button>
          ) : (
            <Button variant="outline" fullWidth onPress={handlePrimaryAction} isDisabled={!shortCode}>
              View {isPast ? "recap" : "details"}
            </Button>
          )}

          <Tabs variant="secondary" selectedKey={activeTab || "overview"} onSelectionChange={setActiveTab}>
            <Tabs.ListContainer className="bg-transparent">
              <Tabs.List>
                {Object.values(TABS).map((tab) => (
                  <Tabs.Tab key={tab.key} id={tab.key}>
                    {tab.label}
                    <Tabs.Indicator />
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs.ListContainer>

            <Tabs.Panel id="overview" className="py-3">
              {/* Only the host can edit — for everyone else the rows render
                  as plain, non-interactive detail rows. */}
              <LoopOverviewPanel
                record={record}
                readonly={!isHost}
                onEdit={() => setShowEdit(true)}
              />
            </Tabs.Panel>
            <Tabs.Panel id="meetings">
              <LoopSeriesPanel record={record} mode="meetings" />
            </Tabs.Panel>
            <Tabs.Panel id="recordings">
              <LoopSeriesPanel record={record} mode="recordings" />
            </Tabs.Panel>
            <Tabs.Panel id="details">
              <div className="py-4"><p>{record.description || "No description"}</p><p>Created by {record.created_by?.user_email || "Unknown"}</p><p>{record.members?.length || 0} participants</p></div>
            </Tabs.Panel>
          </Tabs>
        </Card.Content>
      </Card>

      <EditMeetingDetailsDrawer
        show={showEdit}
        record={record}
        onResp={(action, updated) => {
          if (action === "close") setShowEdit(false);
          if (action === "success") {
            setShowEdit(false);
            // Trust the server's version over the local draft.
            if (updated) setRecord(updated);
            setLocalRefresh((n) => n + 1);
          }
        }}
      />
    </>
  );
};

export default LoopDetailsComp;